import { Box, Flex, xcss } from '@atlaskit/primitives';
import { uploadStaticAssets } from '@atlassian/mpac-api-client';
import { ImageField, TextInput } from '@atlassian/mpac-ui';
import React from 'react';

import type { MultiStepInitialContent } from '@atlassian/mpac-partners';
import type { BannerConfigurationType } from '@atlassian/mpac-types';
import type { ImageUrlType } from '@atlassian/mpac-ui';

const flexStyles = xcss({
  gap: 'space.300',
  flexDirection: 'column',
});

const inputBoxStyles = xcss({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 'space.400',
});

const imageBoxStyles = xcss({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 'space.300',
});

export type SpotlightProps = {
  value: MultiStepInitialContent &
    BannerConfigurationType & {
      screenshot?: ImageUrlType;
    };
  onFieldChange: (
    fieldName: string,
    id: string
  ) => (newValue: string | ImageUrlType | boolean) => void;
  isDisabled?: boolean;
};

const getImageFieldUrl = (imageUrl?: string) => (imageUrl ? { imageUrl } : {});

const SpotlightSlideConfig = (props: SpotlightProps) => {
  const { value, onFieldChange, isDisabled } = props;

  const handleImageFieldChange = (fieldName: string, id: string) => (e: { imageUrl?: string }) => {
    onFieldChange(fieldName, id)(e.imageUrl || '');
  };

  return (
    <Flex xcss={flexStyles}>
      {/* Text inputs */}
      <Box xcss={inputBoxStyles}>
        <TextInput
          label="Eyebrow Text"
          placeholder="Eyebrow text for the banner (optional)"
          defaultValue={value.eyebrowText}
          name={`eyebrowText_${value.id}`}
          onChange={onFieldChange('eyebrowText', value.id)}
        />
        <TextInput
          label="Heading"
          placeholder="Heading for the banner"
          isRequired
          defaultValue={value.heading}
          name={`heading_${value.id}`}
          onChange={onFieldChange('heading', value.id)}
          applyDefaultValidation
        />
        <TextInput
          label="Sub Heading"
          placeholder="Sub Heading for the banner"
          isRequired
          defaultValue={value.subHeading}
          name={`subHeading_${value.id}`}
          onChange={onFieldChange('subHeading', value.id)}
          applyDefaultValidation
        />
        <TextInput
          label="CTA Text"
          placeholder="CTA button text"
          isRequired
          defaultValue={value.ctaText}
          name={`ctaText_${value.id}`}
          onChange={onFieldChange('ctaText', value.id)}
          applyDefaultValidation
        />
        <TextInput
          label="CTA Redirection Link"
          placeholder="CTA button link"
          isRequired
          defaultValue={value.redirectUrl}
          name={`redirectUrl_${value.id}`}
          onChange={onFieldChange('redirectUrl', value.id)}
          applyDefaultValidation
        />
      </Box>

      {/* Images */}
      <Box xcss={imageBoxStyles}>
        <ImageField
          label="Desktop Image"
          name={`desktopbannerImage_${value.id}`}
          customFileName="desktopTileImage"
          uploadFileHandler={uploadStaticAssets}
          isRequired
          url={getImageFieldUrl(value.desktopbannerImage)}
          onChange={handleImageFieldChange('desktopbannerImage', value.id)}
          enableImageDrop
          imageType="hero"
          isDisabled={isDisabled}
        />
        <ImageField
          label="Tablet Image"
          name={`tabletbannerImage_${value.id}`}
          customFileName="tabletTileImage"
          uploadFileHandler={uploadStaticAssets}
          isRequired
          url={getImageFieldUrl(value.tabletbannerImage)}
          onChange={handleImageFieldChange('tabletbannerImage', value.id)}
          enableImageDrop
          imageType="hero"
          isDisabled={isDisabled}
        />
        <ImageField
          label="Mobile Image"
          name={`mobilebannerImage_${value.id}`}
          customFileName="mobileTileImage"
          uploadFileHandler={uploadStaticAssets}
          isRequired
          url={getImageFieldUrl(value.mobilebannerImage)}
          onChange={handleImageFieldChange('mobilebannerImage', value.id)}
          enableImageDrop
          imageType="hero"
          isDisabled={isDisabled}
        />
      </Box>
    </Flex>
  );
};

export default SpotlightSlideConfig;
